import { useState } from "react";
import {
  Mail,
  Eye,
  ChevronDown,
  ChevronRight,
  Clock,
  BarChart3,
  Send,
  CheckCircle2,
  AlertCircle,
  Copy,
  ExternalLink,
} from "lucide-react";
import { EMAIL_SEQUENCES, EMAIL_SEQUENCE_MAP, SEQUENCE_META } from "../data/emailSequences";

import { useTranslation } from 'react-i18next';
// ─── Mock delivery stats ──────────────────────────────────────────────
const MOCK_STATS = {
  totalSent: 12847,
  openRate: 68.2,
  clickRate: 34.7,
  unsubscribeRate: 0.8,
  bySequence: {
    welcome: { sent: 5230, openRate: 74.1, clickRate: 42.3 },
    activation: { sent: 4180, openRate: 63.5, clickRate: 31.8 },
    retention: { sent: 3437, openRate: 65.9, clickRate: 28.4 },
  },
};

// ─── Sequence Card ────────────────────────────────────────────────────
function SequenceCard({ sequenceKey, emails, meta, stats, isExpanded, onToggle }) {
  const [previewEmail, setPreviewEmail] = useState(null);

  const sequenceColors = {
    welcome: { accent: "#F59E0B", bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.2)" },
    activation: { accent: "#22C55E", bg: "rgba(34,197,94,0.08)", border: "rgba(34,197,94,0.2)" },
    retention: { accent: "#3B82F6", bg: "rgba(59,130,246,0.08)", border: "rgba(59,130,246,0.2)" },
  };
  const colors = sequenceColors[sequenceKey] || sequenceColors.welcome;

  return (
    <div style={{ border: "1px solid #27272A", backgroundColor: "#09090B" }}>
      {/* Sequence Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-950/50 transition-colors"
        data-testid={`sequence-toggle-${sequenceKey}`}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-10 h-10 flex items-center justify-center"
            style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
          >
            <Mail className="w-5 h-5" style={{ color: colors.accent }} />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <span className="font-display text-lg uppercase text-white">{meta.label}</span>
              <span
                className="font-mono text-[9px] uppercase tracking-wider px-2 py-0.5"
                style={{ color: colors.accent, border: `1px solid ${colors.border}` }}
              >
                {meta.emailCount} emails
              </span>
            </div>
            <p className="font-mono text-[11px] text-zinc-500 mt-1">{meta.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="hidden md:flex items-center gap-4 text-right">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Sent</div>
              <div className="font-mono text-sm text-zinc-300">{stats?.sent?.toLocaleString() || "—"}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Open</div>
              <div className="font-mono text-sm text-zinc-300">{stats?.openRate ? `${stats.openRate}%` : "—"}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Click</div>
              <div className="font-mono text-sm text-zinc-300">{stats?.clickRate ? `${stats.clickRate}%` : "—"}</div>
            </div>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-zinc-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-zinc-500" />
          )}
        </div>
      </button>

      {/* Expanded: Email List */}
      {isExpanded && (
        <div className="border-t border-zinc-900">
          {emails.map((email, idx) => (
            <div
              key={email.id}
              className="flex items-center justify-between p-4 border-b border-zinc-900/50 last:border-b-0 hover:bg-zinc-950/30 transition-colors"
            >
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <div
                  className="w-8 h-8 flex items-center justify-center flex-shrink-0 font-mono text-xs"
                  style={{ backgroundColor: colors.bg, color: colors.accent, border: `1px solid ${colors.border}` }}
                >
                  {idx + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-sm uppercase text-zinc-200 truncate">{email.subject}</div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="font-mono text-[10px] text-zinc-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {email.delayDays === 0 ? "Immediate" : `Day ${email.delayDays}`}
                    </span>
                    <span className="font-mono text-[10px] text-zinc-600">ID: {email.id}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setPreviewEmail(email)}
                  className="border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-400 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider transition-colors"
                  data-testid={`preview-email-${email.id}`}
                >
                  <Eye className="w-3 h-3 inline mr-1" />Preview
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Email Preview Modal */}
      {previewEmail && (
        <EmailPreviewModal email={previewEmail} onClose={() => setPreviewEmail(null)} />
      )}
    </div>
  );
}

// ─── Email Preview Modal ──────────────────────────────────────────────
function EmailPreviewModal({ email, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopyHtml = () => {
    navigator.clipboard.writeText(email.bodyHtml).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#09090B] border border-zinc-800 w-full max-w-4xl my-8">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-1">EMAIL PREVIEW</div>
            <div className="font-display text-lg uppercase text-white truncate">{email.subject}</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <button
              onClick={handleCopyHtml}
              className="border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-400 px-3 py-2 font-mono text-[10px] uppercase tracking-wider transition-colors"
              data-testid="copy-html-button"
            >
              <Copy className="w-3 h-3 inline mr-1" />
              {copied ? "Copied!" : "Copy HTML"}
            </button>
            <button
              onClick={onClose}
              className="text-zinc-500 hover:text-white px-2 py-2 font-mono text-xs"
            >{t('email_sequence_manager.close')}</button>
          </div>
        </div>

        {/* Email Meta */}
        <div className="px-6 py-3 border-b border-zinc-900 flex items-center gap-6 flex-wrap">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Sequence: </span>
            <span className="font-mono text-[11px] text-zinc-300 capitalize">{email.sequence}</span>
          </div>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Step: </span>
            <span className="font-mono text-[11px] text-zinc-300">{email.step}</span>
          </div>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Delay: </span>
            <span className="font-mono text-[11px] text-zinc-300">{email.delayDays === 0 ? "Immediate" : `Day ${email.delayDays}`}</span>
          </div>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">ID: </span>
            <span className="font-mono text-[11px] text-zinc-300">{email.id}</span>
          </div>
        </div>

        {/* Preview iframe */}
        <div className="p-6">
          <div className="border border-zinc-800 bg-white">
            <iframe
              srcDoc={email.bodyHtml}
              title={`Preview: ${email.subject}`}
              className="w-full"
              style={{ minHeight: "600px", border: "none" }}
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Stats Overview ───────────────────────────────────────────────────
function StatsOverview({ stats }) {
  const cards = [
    { label: "Total Sent", value: stats.totalSent.toLocaleString(), icon: Send, color: "#F59E0B" },
    { label: "Open Rate", value: `${stats.openRate}%`, icon: Eye, color: "#22C55E" },
    { label: "Click Rate", value: `${stats.clickRate}%`, icon: BarChart3, color: "#3B82F6" },
    { label: "Unsubscribe", value: `${stats.unsubscribeRate}%`, icon: AlertCircle, color: "#EF4444" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.label}
            className="border border-zinc-900 p-5"
            style={{ backgroundColor: "#09090B" }}
            data-testid={`stat-${card.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="flex items-center justify-between mb-3">
              <Icon className="w-5 h-5" style={{ color: card.color }} />
              <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-600">MOCK</span>
            </div>
            <div className="font-mono text-2xl font-bold text-white">{card.value}</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{card.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Integration Status ───────────────────────────────────────────────
function IntegrationStatus() {
  const integrations = [
    { name: "SendGrid", status: "not_configured", color: "#52525B" },
    { name: "Mailgun", status: "not_configured", color: "#52525B" },
    { name: "Postmark", status: "not_configured", color: "#52525B" },
  ];

  return (
    <div className="border border-zinc-900 p-5 mb-8" style={{ backgroundColor: "#09090B" }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-1">// ESP INTEGRATION</div>
          <h3 className="font-display text-lg uppercase text-white">{t('email_sequence_manager.email_service_provider')}</h3>
        </div>
        <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 border border-zinc-800 px-2 py-1">{t('email_sequence_manager.mock_data')}</span>
      </div>
      <div className="space-y-3">
        {integrations.map((esp) => (
          <div key={esp.name} className="flex items-center justify-between py-2 border-b border-zinc-900/50 last:border-b-0">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: esp.color }} />
              <span className="font-mono text-sm text-zinc-300">{esp.name}</span>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{t('email_sequence_manager.not_configured')}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-zinc-900">
        <p className="font-mono text-[11px] text-zinc-500 leading-relaxed">
          To enable real email delivery, configure an ESP API key in the backend environment.
          Templates are ready for SendGrid, Mailgun, or any SMTP-compatible provider.
        </p>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────
export default function EmailSequenceManager() {
  const { t } = useTranslation();
  const [expandedSequences, setExpandedSequences] = useState({
    welcome: true,
    activation: false,
    retention: false,
  });

  const toggleSequence = (key) => {
    setExpandedSequences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const totalEmails = EMAIL_SEQUENCES.length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">
            // ADMIN · EMAIL SEQUENCES
          </div>
          <h2 className="font-display text-3xl uppercase">{t('email_sequence_manager.email_automation')}</h2>
          <p className="font-mono text-[11px] text-zinc-500 mt-2">
            {totalEmails} templates across 3 sequences · Mock data mode
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-amber-500 border border-amber-500/30 px-3 py-1.5">
            <CheckCircle2 className="w-3 h-3" />
            {totalEmails} templates active
          </span>
        </div>
      </div>

      {/* Stats Overview */}
      <StatsOverview stats={MOCK_STATS} />

      {/* Integration Status */}
      <IntegrationStatus />

      {/* Sequences */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">
          // SEQUENCES
        </div>
        <div className="space-y-4">
          {Object.entries(EMAIL_SEQUENCE_MAP).map(([key, emails]) => (
            <SequenceCard
              key={key}
              sequenceKey={key}
              emails={emails}
              meta={SEQUENCE_META[key]}
              stats={MOCK_STATS.bySequence[key]}
              isExpanded={expandedSequences[key]}
              onToggle={() => toggleSequence(key)}
            />
          ))}
        </div>
      </div>

      {/* Footer note */}
      <div className="mt-8 pt-6 border-t border-zinc-900">
        <p className="font-mono text-[11px] text-zinc-600 leading-relaxed">
          All email templates use inline CSS for maximum email client compatibility.
          Dark theme with amber accents consistent with Max OS Cloud brand.
          Templates are HTML5 compliant and tested against major email clients (Gmail, Outlook, Apple Mail).
        </p>
      </div>
    </div>
  );
}
